import { useMemo } from 'react';
import plur from 'plur';
import { Paragraph, Strong, useConfirmationModal, useSnackbars } from '@customerio/pluma-components/react';
import type { DeliveriesDeliveryRetryRequest } from '@customerio/hydra-ui-api';
import type { Delivery } from '@/services/db/delivery/entity';
import {
  useDeleteDeliveries,
  useRedeliverDeliveries,
  useRetryDeliveries,
  useSendDeliveries,
} from '@/services/db/delivery/mutations';
import { getDisplayState } from '@/services/db/delivery/utils';
import { useAnalytics } from '@/services/analytics';
import { itemLabelFor } from './utils/action-labels';
import { runBulkVerb } from './utils/run-bulk-verb';

type RetryConfirmMessageProps = { count: number; label: string };

function RetryConfirmMessage({ count, label }: RetryConfirmMessageProps) {
  return (
    <Paragraph>
      {`You are about to retry ${count} ${label}. It may take some time to process your request and this cannot be undone.`}
    </Paragraph>
  );
}

type DeleteConfirmMessageProps = { count: number; label: string };

function DeleteConfirmMessage({ count, label }: DeleteConfirmMessageProps) {
  return (
    <Paragraph>
      {`You're about to delete ${count} ${label}. This `}
      <Strong>cannot</Strong>
      {' be undone.'}
    </Paragraph>
  );
}

export interface UseDeliveryActionsInput {
  /** The currently-loaded rows — used to derive the item label for copy. */
  deliveries: readonly Delivery[];
  /** The active status filter, sent as the retry `metric` (Ember parity); typed from the endpoint. */
  deliveryStatus?: DeliveriesDeliveryRetryRequest['metric'] | null;
  workspaceId?: number;
}

/** Options for a resend — the tracking preference chosen in the Resend modal. */
export interface RedeliverOptions {
  tracked?: boolean;
}

export interface DeliveryActions {
  onBulkSend: (ids: string[]) => void;
  onBulkRetry: (ids: string[]) => void;
  onBulkRedeliver: (ids: string[], options?: RedeliverOptions) => void;
  /** Bulk delete: confirms first (Ember `promptBulkDestroy` always confirms a bulk destroy). */
  onBulkDelete: (ids: string[]) => void;
  /** Per-row retry: fires directly with no confirmation, matching the Ember expanded row. */
  onRowRetry: (ids: string[]) => void;
  /** Per-row delete: fires directly with no confirmation, matching the Ember expanded row. */
  onRowDelete: (ids: string[]) => void;
}

type ConfirmOptions = Parameters<ReturnType<typeof useConfirmationModal>>[0];
type BulkVerb = Parameters<typeof runBulkVerb>[1];

/** Bulk-retry confirmation dialog options (Ember always confirms a bulk retry). */
function retryConfirmOptions(deliveries: readonly Delivery[], ids: string[]): ConfirmOptions {
  const label = plur(itemLabelFor(deliveries, ids), ids.length);
  return {
    title: 'Retry messages?',
    message: <RetryConfirmMessage count={ids.length} label={label} />,
    confirmLabel: `Retry ${label}`,
  };
}

/** Bulk-delete confirmation dialog options (Ember `promptBulkDestroy`). */
function deleteConfirmOptions(deliveries: readonly Delivery[], ids: string[]): ConfirmOptions {
  const label = plur(itemLabelFor(deliveries, ids), ids.length);
  return {
    title: `Delete ${label}?`,
    isCritical: true,
    confirmLabel: `Delete ${label}`,
    message: <DeleteConfirmMessage count={ids.length} label={label} />,
  };
}

/**
 * Owns the Outbox bulk actions: wires the delivery mutations to the table's
 * bulk callbacks with the three-state snackbar choreography (`runBulkVerb`).
 * Bulk retry first opens a confirmation modal (Ember always confirms a bulk
 * retry). Optimistic pending marking is handled inside the mutation hooks.
 *
 * SCOPE: the messaging-limit override (`override_message_limit`) + its retry-
 * modal radio/banner are deferred — the data layer's `retryDeliveries` has no
 * override field and there's no message-limit resource yet.
 *
 * @public
 */
export function useDeliveryActions({
  deliveries,
  deliveryStatus,
  workspaceId,
}: UseDeliveryActionsInput): DeliveryActions {
  const snackbars = useSnackbars();
  const confirm = useConfirmationModal();
  const { track } = useAnalytics();
  // Destructure the stable `mutateAsync` fns — the mutation objects themselves
  // change identity every render (isPending), which would defeat the callback
  // memoization below and remount the expanded row on each render.
  const { mutateAsync: sendAsync } = useSendDeliveries({ workspaceId });
  const { mutateAsync: retryAsync } = useRetryDeliveries({ workspaceId });
  const { mutateAsync: redeliverAsync } = useRedeliverDeliveries({ workspaceId });
  const { mutateAsync: deleteAsync } = useDeleteDeliveries({ workspaceId });

  return useMemo((): DeliveryActions => {
    const run = (verb: BulkVerb, ids: string[], mutate: () => Promise<unknown>) =>
      runBulkVerb(snackbars, verb, itemLabelFor(deliveries, ids), ids.length, mutate);
    const runRetry = (ids: string[]) =>
      run('retry', ids, () => retryAsync({ deliveryIds: ids, metric: deliveryStatus ?? '' }));
    const runDelete = (ids: string[]) => run('delete', ids, () => deleteAsync({ deliveryIds: ids }));
    const confirmThenRun = async (options: ConfirmOptions, action: () => Promise<unknown>) => {
      if (await confirm(options)) await action();
    };

    // Retry-funnel analytics (Ember `_trackRetryEvent`): `count` + the unique
    // display states of the affected rows. The messaging-limit override is
    // deferred, so `Override Message Limit` is always false.
    const retryProps = (ids: string[]) => {
      const statuses = new Set<string>();
      for (const delivery of deliveries) {
        if (!ids.includes(delivery.id)) continue;
        const state = getDisplayState(delivery);
        if (state != null) statuses.add(state);
      }
      return { 'Selected Message Count': ids.length, 'Selected Statuses': [...statuses] };
    };
    const trackRetryConfirmed = (ids: string[]) =>
      void track('message', 'retrySendConfirmed', { ...retryProps(ids), 'Override Message Limit': false });

    return {
      onBulkSend: (ids) => void run('send', ids, () => sendAsync({ deliveryIds: ids })),
      onBulkRedeliver: (ids, options) =>
        void run('redeliver', ids, () => redeliverAsync({ deliveryIds: ids, tracked: options?.tracked })),
      // A bulk retry confirms first: Started fires once the modal is open, then Confirmed
      // or Canceled on the close reason (Ember `promptBulkRetry` opens the modal, then
      // tracks Started; `waitForClose` drives the rest). Confirmed fires on the confirm
      // itself, alongside kicking off the retry — not gated on the request settling.
      onBulkRetry: (ids) =>
        void (async () => {
          const confirmed = confirm(retryConfirmOptions(deliveries, ids));
          void track('message', 'retrySendStarted', retryProps(ids));
          if (await confirmed) {
            trackRetryConfirmed(ids);
            void runRetry(ids);
          } else {
            void track('message', 'retrySendCanceled', retryProps(ids));
          }
        })(),
      // A per-row retry has no confirm step (Ember's direct path), so it only fires Confirmed.
      onRowRetry: (ids) => {
        void runRetry(ids);
        trackRetryConfirmed(ids);
      },
      // Bulk delete confirms (Ember `promptBulkDestroy`); the expanded-row delete fires directly
      // (Ember row `@onClick={{@onDelete}}`), so a cancelled confirm can't strand the row's latch.
      onBulkDelete: (ids) => void confirmThenRun(deleteConfirmOptions(deliveries, ids), () => runDelete(ids)),
      onRowDelete: (ids) => void runDelete(ids),
    };
  }, [snackbars, confirm, track, deliveries, deliveryStatus, sendAsync, retryAsync, redeliverAsync, deleteAsync]);
}
